import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, TrendingUp, Receipt, Wrench, Building2, Heart, ShieldCheck } from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";

export default function Impact() {
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/impact-stats`)
      .then((r) => setImpact(r.data))
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") console.warn("impact stats offline:", err?.message);
      });
  }, []);

  const stats = [
    {
      icon: Wrench,
      label: "Work orders closed this month",
      value: impact ? String(impact.work_orders_closed_this_month) : "—",
      hint: "resolved or closed cases",
      testid: "impact-wo",
    },
    {
      icon: ShieldCheck,
      label: "AI contracts drafted",
      value: impact ? String(impact.ai_contracts_generated) : "—",
      hint: "auto-generated agreements",
      testid: "impact-contracts",
    },
    {
      icon: Receipt,
      label: "AI collection notices",
      value: impact ? String(impact.ai_notices_sent) : "—",
      hint: "letters that never touched a lawyer's desk",
      testid: "impact-notices",
    },
    {
      icon: TrendingUp,
      label: "Under AI project management",
      value: impact ? `$${(impact.dollars_managed / 1000).toFixed(1)}k` : "—",
      hint: "sum of active contract values",
      testid: "impact-dollars",
    },
  ];

  return (
    <div className="min-h-screen paper-texture text-foreground" data-testid="impact-page">
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-2 grid grid-cols-3 items-center">
        <div />
        <Link to="/" className="flex flex-col items-center justify-self-center">
          <CommunityIQLogo className="w-56 h-56 -my-10" />
        </Link>
        <div className="flex items-center gap-3 justify-self-end">
          <Link to="/pricing" className="text-sm font-medium text-gold hover:brightness-125 transition-all">Pricing</Link>
          <Link to="/register" className="text-sm font-medium bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/85 transition-colors glow-purple">Get started</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-16 text-center">
        <div className="inline-flex items-center gap-2 bg-accent/60 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border border-gold">
          <Sparkles className="w-3.5 h-3.5 text-gold" />
          <span className="text-gold">Transparent impact · updated hourly</span>
        </div>
        <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight mb-4 leading-[1.05]">
          <span className="royal-gradient-text">Every dollar we save the board</span>
          <br />
          <span className="text-gold">stays with the community.</span>
        </h1>
        <p className="no-gold text-foreground/75 max-w-2xl mx-auto text-lg">
          CommunityIQ is a mission-driven platform. These numbers are live — pulled from real boards using AI Saint
          right now. No spin, no marketing math.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="hoa-card royal-ring p-6" data-testid={s.testid}>
              <div className="w-11 h-11 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="text-4xl font-semibold text-gold font-mono">{s.value}</div>
              <div className="text-sm font-medium mt-1">{s.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.hint}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="hoa-card royal-ring p-8 md:p-10">
          <div className="w-12 h-12 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center mb-4">
            <Heart className="w-6 h-6" />
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-3">
            <span className="royal-gradient-text">Where the savings go</span>
          </h2>
          <p className="no-gold text-foreground/85 leading-relaxed mb-6">
            Every AI contract replaces roughly <span className="text-gold font-semibold">$2,400</span> in legal fees.
            Every AI bid comparison typically saves <span className="text-gold font-semibold">10–25%</span> in change-order creep.
            The dollars stay in the community — funding playgrounds, roof replacements, curb appeal, and reserves.
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div className="p-4 rounded-xl bg-accent/30 border border-border">
              <Building2 className="w-5 h-5 text-gold mb-2" />
              <div className="font-semibold text-gold">Reserves</div>
              <p className="text-foreground/75 text-xs mt-1">Roofs · siding · pool decks · asphalt</p>
            </div>
            <div className="p-4 rounded-xl bg-accent/30 border border-border">
              <Wrench className="w-5 h-5 text-gold mb-2" />
              <div className="font-semibold text-gold">Repairs done right</div>
              <p className="text-foreground/75 text-xs mt-1">Vetted bids · insured contractors · warranties on file</p>
            </div>
            <div className="p-4 rounded-xl bg-accent/30 border border-border">
              <Heart className="w-5 h-5 text-gold mb-2" />
              <div className="font-semibold text-gold">Community morale</div>
              <p className="text-foreground/75 text-xs mt-1">Kudos · events · clear communications</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24 text-center">
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          <span className="royal-gradient-text">Bring CommunityIQ</span> to your board.
        </h2>
        <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-8">
          Free forever on essentials. Start any add-on with a 14-day trial. Nonprofit HOAs save 20% on yearly.
        </p>
        <Link to="/register" data-testid="impact-cta" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-8 py-4 rounded-full font-semibold hover:brightness-110 transition-all glow-gold">
          Start free <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} CommunityIQ · Communities beautifully managed.
        </div>
      </footer>
    </div>
  );
}
